import React, { useState, useEffect } from 'react'
import * as api from '../../../app/axios/api' // Make sure this path is correct
import { Trash2, X, Plus, BookOpen } from 'lucide-react'
import { toast } from 'sonner'

const SummerCourse = () => {
  const [data, setData] = useState([])

  // Initial form state matching your Go Struct
  const initialFormState = {
    course_mode: 'offline', // Default Enum
    branch: '',
    academic_courses: '',
    fee: '',
    registration_fee: '',
    discount: '',
    gender_discount: '',
    session: '',
    semester: '',
    course_code: '',
  }

  const [formData, setFormData] = useState(initialFormState)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [showCreateFormModal, setShowCreateFormModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [courseToDelete, setCourseToDelete] = useState(null)

  // 1. Fetch Courses
  const fetchData = async () => {
    setFetchLoading(true)
    try {
      const res = await api.fetchCourse()
      // Assuming res.data contains the array of courses
      setData(res.data || [])
    } catch (error) {
      console.error('Error fetching course data:', error)
      toast.error('Failed to fetch courses')
    } finally {
      setFetchLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // 2. Create Course
  const handleCreateCourse = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      // Format payload: Convert number fields to integers (Int64 in Go)
      const payload = {
        ...formData,
        fee: parseInt(formData.fee) || 0,
        registration_fee: parseInt(formData.registration_fee) || 0,
        discount: parseInt(formData.discount) || 0,
        gender_discount: parseInt(formData.gender_discount) || 0,
      }

      await api.createCourse(payload)

      toast.success('Summer Course created successfully!')

      setFormData(initialFormState) // Reset form
      setShowCreateFormModal(false) // Close Modal
      fetchData() // Refresh Table
    } catch (error) {
      console.error('Error creating course:', error)
      toast.error('Error creating course. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Delete Click (Open Modal)
  const handleDeleteClick = (item) => {
    setCourseToDelete(item)
    setShowDeleteConfirmModal(true)
  }

  // 3. Confirm Delete Course
  const confirmDelete = async () => {
    if (!courseToDelete) return

    setLoading(true)
    try {
      // Assumes your course object has an 'id' field from DB
      await api.deleteCourse(courseToDelete.id)

      toast.success(
        `Course "${courseToDelete.academic_courses}" deleted successfully!`,
      )

      setShowDeleteConfirmModal(false)
      setCourseToDelete(null)
      fetchData() // Refresh Table
    } catch (error) {
      console.error('Error deleting course:', error)
      toast.error('Error deleting course. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4 md:p-6'>
      <div className='max-w-[1400px] mx-auto'>
        {/* Header */}
        <div className='bg-white rounded-lg shadow-lg p-3 sm:p-4 md:p-6 mb-4 md:mb-6'>
          <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4'>
            <div>
              <h1 className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2'>
                <BookOpen className='text-indigo-600' size={28} />
                Summer Course Management
              </h1>
              <p className='text-sm sm:text-base text-gray-600 mt-1'>
                Create, view, and manage academic summer courses.
              </p>
            </div>
            <button
              onClick={() => setShowCreateFormModal(true)}
              className='w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition shadow-lg flex items-center justify-center gap-2'
            >
              <Plus size={20} />
              Add Course
            </button>
          </div>
        </div>

        {/* Create Form Modal (Remaining unchanged, keeping inputs in grid format) */}
        {showCreateFormModal && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto'>
              <div className='sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center z-10'>
                <h2 className='text-xl sm:text-2xl font-semibold text-gray-800'>
                  Add New Summer Course
                </h2>
                <button
                  onClick={() => setShowCreateFormModal(false)}
                  className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                >
                  <X size={24} className='text-gray-600' />
                </button>
              </div>

              <form onSubmit={handleCreateCourse} className='p-4 sm:p-6'>
                {/* --- Form fields are wrapped in a 3-column grid --- */}
                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Course Code *
                    </label>
                    <input
                      type='text'
                      name='course_code'
                      value={formData.course_code}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='e.g. CS101'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Course Name *
                    </label>
                    <input
                      type='text'
                      name='academic_courses'
                      value={formData.academic_courses}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='e.g. Web Development'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Course Mode *
                    </label>
                    <select
                      name='course_mode'
                      value={formData.course_mode}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                    >
                      <option value='online'>Online</option>
                      <option value='offline'>Offline</option>
                    </select>
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Branch *
                    </label>
                    <input
                      type='text'
                      name='branch'
                      value={formData.branch}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='e.g. Computer Science'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Session *
                    </label>
                    <input
                      type='text'
                      name='session'
                      value={formData.session}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='e.g. 2024-2025'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Semester *
                    </label>
                    <input
                      type='text'
                      name='semester'
                      value={formData.semester}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='e.g. 4th Sem'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Fee (₹) *
                    </label>
                    <input
                      type='number'
                      name='fee'
                      value={formData.fee}
                      onChange={handleChange}
                      required
                      min='0'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='0'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Registration Fee (₹) *
                    </label>
                    <input
                      type='number'
                      name='registration_fee'
                      value={formData.registration_fee}
                      onChange={handleChange}
                      required
                      min='0'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='0'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Discount (₹)
                    </label>
                    <input
                      type='number'
                      name='discount'
                      value={formData.discount}
                      onChange={handleChange}
                      min='0'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='0'
                    />
                  </div>
                  <div className='col-span-1'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Gender Discount (₹)
                    </label>
                    <input
                      type='number'
                      name='gender_discount'
                      value={formData.gender_discount}
                      onChange={handleChange}
                      min='0'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='0'
                    />
                  </div>
                </div>

                <div className='flex justify-end mt-8'>
                  <button
                    type='submit'
                    disabled={loading}
                    className='w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg'
                  >
                    {loading ? 'Adding Course...' : 'Add Course'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- FULL DATA EXCEL-STYLE TABLE --- */}
        <div className='bg-white rounded-lg shadow-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            {/* "whitespace-nowrap" use kiya hai taki table columns clear dikhein, compress na ho */}
            <table className='w-full border-collapse whitespace-nowrap'>
              <thead>
                <tr className='bg-gradient-to-r from-indigo-600 to-purple-600 text-white'>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Code
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Course Name
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Mode
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Branch
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Session
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Semester
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Fee (₹)
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Reg. Fee (₹)
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Discount (₹)
                  </th>
                  {/* <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Gen. Disc. (₹)
                  </th> */}
                  <th className='px-3 py-3 text-center text-xs sm:text-sm font-semibold border border-gray-300  bg-purple-600 z-10 shadow-[-4px_0_10px_rgba(0,0,0,0.1)]'>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {fetchLoading ? (
                  <tr>
                    <td
                      colSpan={11}
                      className='px-3 py-8 text-center text-gray-600 border border-gray-300'
                    >
                      Loading summer courses...
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className='px-3 py-8 text-center text-gray-600 border border-gray-300'
                    >
                      No courses found. Start by adding one!
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr
                      key={item.id || index}
                      className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-indigo-50 transition-colors`}
                    >
                      {/* Course Code */}
                      <td className='px-3 py-3 text-xs sm:text-sm font-bold text-gray-800 border border-gray-300 uppercase'>
                        {item.course_code}
                      </td>

                      {/* Academic Courses */}
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300 font-medium'>
                        {item.academic_courses}
                      </td>

                      {/* Course Mode */}
                      <td className='px-3 py-3 text-xs sm:text-sm border border-gray-300'>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            item.course_mode?.toLowerCase() === 'online'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {item.course_mode || 'N/A'}
                        </span>
                      </td>

                      {/* Branch */}
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300'>
                        {item.branch || 'N/A'}
                      </td>

                      {/* Session */}
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300'>
                        {item.session || 'N/A'}
                      </td>

                      {/* Semester */}
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300'>
                        {item.semester || 'N/A'}
                      </td>

                      {/* Fee */}
                      <td className='px-3 py-3 text-xs sm:text-sm font-semibold text-gray-800 border border-gray-300'>
                        ₹{item.fee?.toLocaleString('en-IN') || 0}
                      </td>

                      {/* Registration Fee */}
                      <td className='px-3 py-3 text-xs sm:text-sm font-semibold text-gray-800 border border-gray-300'>
                        ₹{item.registration_fee?.toLocaleString('en-IN') || 0}
                      </td>

                      {/* Discount */}
                      <td className='px-3 py-3 text-xs sm:text-sm text-red-600 font-medium border border-gray-300'>
                        ₹{item.discount?.toLocaleString('en-IN') || 0}
                      </td>

                      {/* Gender Discount */}
                      {/* <td className='px-3 py-3 text-xs sm:text-sm text-pink-600 font-medium border border-gray-300'>
                        ₹{item.gender_discount?.toLocaleString('en-IN') || 0}
                      </td> */}

                      {/* Action Button - Sticky right for easy scrolling */}
                      <td
                        className={`px-3 py-3 text-center border border-gray-300  z-10 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} shadow-[-4px_0_10px_rgba(0,0,0,0.05)]`}
                      >
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className='px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors flex items-center gap-1 mx-auto'
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmModal && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-2xl max-w-md w-full p-6'>
              <div className='flex items-center gap-3 mb-4'>
                <div className='p-3 bg-red-100 rounded-full'>
                  <Trash2 size={24} className='text-red-600' />
                </div>
                <h3 className='text-xl font-semibold text-gray-800'>
                  Confirm Delete
                </h3>
              </div>
              <p className='text-gray-600 mb-6'>
                Are you sure you want to delete the course{' '}
                <span className='font-bold'>
                  "{courseToDelete?.academic_courses} (
                  {courseToDelete?.course_code})"
                </span>
                ? This action cannot be undone.
              </p>
              <div className='flex gap-3 justify-end'>
                <button
                  onClick={() => setShowDeleteConfirmModal(false)}
                  className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className='px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                  disabled={loading}
                >
                  <Trash2 size={16} />
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SummerCourse
